import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { OpsHistoryShell } from "../components/OpsHistoryShell.js";
import { WarningRow } from "../components/OpsRows.js";

/**
 * Every tolerant-parse warning, filterable by scope and by text.
 *
 * A short tail answers "has something started going wrong"; this page answers *what* is
 * going wrong and how often, where a hundred rows of one message would otherwise crowd out
 * the one that appears twice.
 */
export function WarningsPage() {
  const [scope, setScope] = useState("");
  /**
   * What is typed and what has been asked for are separate, so a search runs on submit
   * rather than on every keystroke.
   */
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const scopes = useQuery({ queryKey: ["warnings", "scopes"], queryFn: api.warningScopes });

  const history = useInfiniteQuery({
    queryKey: ["warnings", "history", { scope, q }],
    queryFn: ({ pageParam }) =>
      api.warningHistory({
        ...(scope ? { scope } : {}),
        ...(q ? { q } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.cursor ?? undefined,
  });

  const items = useMemo(() => history.data?.pages.flatMap((p) => p.items) ?? [], [history.data]);
  const total = history.data?.pages[0]?.total ?? null;
  const filtered = Boolean(scope || q);

  return (
    <OpsHistoryShell
      title="Warnings"
      deck="Where every tolerant parser puts what it could not do. A warning is a degradation that was recorded rather than thrown — the pipeline kept going."
      count={
        total === null
          ? `${items.length} shown`
          : `${items.length} of ${total.toLocaleString()} warning${total === 1 ? "" : "s"}`
      }
      note={filtered ? " — filtered" : null}
      isPending={history.isPending}
      error={history.isError ? (history.error as Error) : null}
      isEmpty={items.length === 0}
      emptyFiltered={filtered}
      emptyAll="Nothing has degraded to null. That is the good case."
      onLoadMore={history.hasNextPage ? () => void history.fetchNextPage() : null}
      loadingMore={history.isFetchingNextPage}
      filters={
        <form
          className="histfilters"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(draft.trim());
          }}
        >
          <label>
            <span className="k">Scope</span>
            {/*
              Only the scopes that have recorded something. Offering one that never has
              reads as a pipeline stage that is broken.
            */}
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">any</option>
              {(scopes.data?.scopes ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="histsearch">
            <span className="k">Message or subject</span>
            <input
              value={draft}
              placeholder="image-only, Ollama, 0001104659-25-010902…"
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button type="submit" className="iconbtn">
            Search
          </button>
          {filtered && (
            <button
              type="button"
              className="iconbtn"
              onClick={() => {
                setScope("");
                setDraft("");
                setQ("");
              }}
            >
              Clear
            </button>
          )}
        </form>
      }
    >
      {items.map((w) => (
        <WarningRow
          key={w.id}
          warning={w}
          open={openId === w.id}
          onToggle={() => setOpenId(openId === w.id ? null : w.id)}
        />
      ))}
    </OpsHistoryShell>
  );
}
